/**
 * Two curated palettes for category identity, picked by category type.
 *
 * The split exists so category dots reinforce the income/expense distinction
 * at a glance: income categories live in the cool/green half of the wheel,
 * expense categories in the warm/red half. The dot says "expense category"
 * and "this specific category" in one mark.
 *
 * Excluded by design from both palettes:
 *   - Amber Signal (38 88% 50%) — reserved for primary CTA + brand accent
 *   - Growth Emerald (158 60% 36%) — reserved for the income semantic marker
 *   - Deficit Red (358 68% 52%) — reserved for the expense semantic marker
 *
 * The category dots therefore SUGGEST the type via hue family, without
 * colliding with the load-bearing semantic colors.
 *
 * Each color is stored as raw HSL components so CSS can apply alpha via
 *   hsl(${color} / 0.14)
 *   hsl(${color})
 * in both light and dark themes without separate values.
 */

/** Cool hues with green leanings — for income categories. */
export const INCOME_CATEGORY_PALETTE = [
  '200 72% 48%', // pacific blue
  '155 28% 48%', // sage
  '175 55% 45%', // teal
  '230 45% 55%', // indigo
] as const;

/** Warm hues — for expense categories. Hues stay clear of the
 *  deficit-red zone (~350–10°) by spanning 15–25° and 320–340°. */
export const EXPENSE_CATEGORY_PALETTE = [
  '15 80% 58%',  // coral
  '25 55% 48%',  // terracotta
  '320 50% 55%', // magenta
  '340 50% 60%', // rose
] as const;

const hash = (str: string): number => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const getCategoryColorHsl = (
  key: string,
  type: 'income' | 'expense',
): string => {
  const palette =
    type === 'income' ? INCOME_CATEGORY_PALETTE : EXPENSE_CATEGORY_PALETTE;
  return palette[hash(key) % palette.length];
};
